import { AUTHENTICATION_METHOD, HTTP_STATUS, USERNAME_FORMAT_CODE, USER_ACTIVATION_METHOD } from '../../domain/domainCodes.js'
import { ApiError, api } from '../../lib/api'
import { isEnglish } from '../../lib/i18n'
import { common as C, documentationStrings as S } from './strings'
import { booleanSelect, loginThemeSelect } from './brandingValues'

/**
 * System settings — `Features/Admin/SettingsManagement/Settings/Endpoint.cs`.
 *
 *   getSettings -> GET /api/settings            -> SettingsPageViewModel (74 fields)
 *   saveSection -> POST /Settings/Save<Section> -> { Result, Message }
 *
 * TWENTY-TWO SAVE ACTIONS, ONE PER CARD. The screen was built that way already — every
 * card has its own save button — so `saveSection` keeps its signature and this module
 * does the routing. `SECTIONS` below is the whole map: a card's id, the action it posts
 * to, and the command shape that action binds.
 *
 * SHAPE. The server answers a FLAT view model (`ArabicApplicationTitle`,
 * `WebsiteFooterText`, …) while the screen reads a settings DICTIONARY keyed by the raw
 * setting names (`Default_ArabicApplicationTitle`, …). `toSettings` below is that
 * translation, and it is the only place the two vocabularies meet — the screen did not
 * change (R2).
 *
 * The `getAdvanced` and `listSettingsKeys` calls still read mock data; they belong to the
 * `AdvancedSettings` and `SettingsKeys` slices, which are integrated separately.
 */

/**
 * View model -> the dictionary `sectionDefaults` reads.
 *
 * The keys are the screen's, not the database's: this dictionary is a client-side shape,
 * and matching it exactly is what let the page stay untouched. Booleans become the
 * strings `"true"`/`"false"` because the selects are `<option value="true">`.
 */
function toSettings(dto) {
  const bool = (value) => (value ? 'true' : 'false')

  return {
    Default_ArabicApplicationTitle: dto.ArabicApplicationTitle ?? '',
    Default_EnglishApplicationTitle: dto.EnglishApplicationTitle ?? '',
    WebsiteFooterText: dto.WebsiteFooterText ?? '',
    WebsiteFooterTextEn: dto.WebsiteFooterTextEn ?? '',
    DomainURL: dto.DomainUrl ?? '',
    UseHsts: (dto.UseHsts ?? 'false').toLowerCase(),
    AdminPanelUserManualUrl: dto.AdminPanelUserManualUrl ?? '',
    ServicesUrl: dto.ServicesUrl ?? '',

    LoginPageTheme: loginThemeSelect(dto.LoginPageTheme),
    LogoOnLoginPage: booleanSelect(dto.LogoOnLoginPage, 'false'),
    LogoBackgroundEnabled: booleanSelect(dto.LogoBackground, 'true'),

    ShowSupportContactInfoPage: bool(dto.ShowSupportContactInfoPage),
    ContactPageTitle: dto.ContactPageTitle ?? '',
    ContactPageTitleEn: dto.ContactPageTitleEn ?? '',
    ContactPageHeader: dto.ContactPageHeader ?? '',
    /* The screen reads a lower-case `h` here. Kept exactly as it asks rather than
       "corrected", because the two have to agree and the screen is the consumer. */
    ContactPageheaderEn: dto.ContactPageHeaderEn ?? '',
    ContactEmail: dto.ContactEmail ?? '',
    ContactEmailShowSendForm: bool(dto.ShowSupportContactSendEmailForm),
    ContactEmailShowEmailInfo: bool(dto.ShowSupportContactEmailInfo),
    ContactEmailShowPhoneInfo: bool(dto.ShowSupportContactPhoneInfo),
    ContactEmailLabelText: dto.ContactEmailLabelText ?? '',
    ContactEmailEnLabelText: dto.ContactEmailEnLabelText ?? '',
    ContactPhone: dto.ContactPhone ?? '',
    ContactPhoneLabelText: dto.ContactPhoneLabelText ?? '',
    ContactPhoneEnLabelText: dto.ContactPhoneEnLabelText ?? '',

    Default_EnablePlayerProgressbar: bool(dto.DefaultEnablePlayerProgressbar),
    Default_EnableForceSubtitle: bool(dto.DefaultEnableForceSubtitle),
    Default_EnablePlayerBlur: bool(dto.DefaultEnablePlayerPauseOnBlur),
    Default_EnablePlayerPlaybackRates: bool(dto.DefaultEnablePlayerPlaybackRates),
    EnableUserSubtitleControl: bool(dto.EnableUserSubtitleControl),

    Default_EnableUserManualPopup: bool(dto.DefaultEnableUserManualPopup),
    EnableIntroVideo: bool(dto.EnableIntroVideo),
    UserGuideLinkOnTheLoginPage: bool(dto.UserGuideLinkOnTheLoginPage),
    ShowUserGuideOnSeparatePage: bool(dto.ShowUserGuideOnSeparatePage),

    Email_Host: dto.EmailHost ?? '',
    Email_Port: dto.EmailPort ?? '',
    Email_From: dto.EmailFrom ?? '',
    Email_SenderName: dto.EmailSenderName ?? '',
    Email_Username: dto.EmailUsername ?? '',
    Email_Password: dto.EmailPassword ?? '',
    Email_SslOptions: dto.EmailSslOptions ?? '',
    Email_ServiceState: dto.EmailServiceState ?? '',
    DisableMailServerCertificateValidation: (dto.DisableMailServerCertificateValidation ?? 'false').toLowerCase(),

    /* Stored as JSON blobs, one per channel. Parsed at the edge so the cards read plain
       fields; `messagingBody` puts them back. */
    WhatsAppSettings: dto.WhatsAppSettings ?? '{}',
    SlackSettings: dto.SlackSettings ?? '{}',
    SmsSettings: dto.SmsSettings ?? '{}',

    User_Statistics_IsEnabled: bool(dto.UserStatisticsIsEnabled),
    Reports_Policies_Profile_Columns: dto.PoliciesReportProfileColumns ?? '',
    Reports_Exams_Profile_Columns: dto.ExamsReportProfileColumns ?? '',
  }
}

export async function getSettings(signal) {
  const dto = (await api.get('/api/settings', { signal })) ?? {}

  return {
    settings: toSettings(dto),
    brand: {
      main: dto.BrandPrimaryColor ?? '',
      active: dto.BrandActiveColor ?? '',
    },
    /* Both names and the email as stored, so the organisation card opens on what it is
       about to overwrite. It opened blank when only the display name came back. */
    company: {
      name: dto.CompanyName ?? '',
      id: dto.CompanyId ?? 0,
      arabicName: dto.CompanyArabicName ?? '',
      englishName: dto.CompanyEnglishName ?? '',
      email: dto.CompanyEmail ?? '',
    },
    /* The user manuals already published — per language and format — so the upload card can
       show the one in place beside each picker. A PDF has no preview to draw. */
    userManuals: (dto.UserManuals ?? []).map((manual) => ({
      language: manual.Language,
      format: manual.Format,
      url: manual.Url,
      size: manual.Size ?? 0,
      updatedAt: manual.UpdatedAt ?? null,
    })),
    /* The server already drops the three event types that carry no editable template
       (phishing, users, groups), so the client-side filter the mock needed is gone. */
    notificationTemplates: (dto.NotificationTemplates ?? []).map((template) => ({
      id: template.Id,
      // The table shows one name; which one depends on the panel's language.
      name: (isEnglish ? template.NameEnglish : template.NameArabic) || template.NameArabic || '',
      subject: template.Subject ?? '',
      content: template.Content ?? '',
      type: template.Type,
      lastUpdated: template.LastUpdatedTxt ?? '',
    })),
  }
}

/**
 * Every card, the action it posts to, and how its form becomes that action's command.
 *
 * Adding a card means adding a row here and nothing else.
 */
const SECTIONS = {
  brandTheme: {
    path: '/Settings/SaveBrandTheme',
    body: (f) => ({ PrimaryColor: f.main, ActiveColor: f.active }),
  },
  appTitle: {
    path: '/Settings/SaveApplicationTitle',
    body: (f) => ({ ArabicTitle: f.arabicAppTitle, EnglishTitle: f.englishAppTitle }),
  },
  footer: {
    path: '/Settings/SaveFooterText',
    body: (f) => ({ ArabicFooter: f.footer, EnglishFooter: f.footerEn }),
  },
  domain: {
    path: '/Settings/SaveDomainUrl',
    body: (f) => ({ Url: f.domainUrl, UseHsts: String(f.useHsts) }),
  },
  loginTheme: {
    path: '/Settings/SaveLoginTheme',
    body: (f) => ({ Theme: f.theme, LogoOnLogin: String(f.logoOnLogin) }),
  },
  organization: {
    path: '/Settings/SaveOrganization',
    body: (f) => ({
      ArabicName: f.arabicOrgName,
      EnglishName: f.englishOrgName,
      Email: f.orgEmail,
    }),
    /* Both names are required — a blank post would erase the organisation's name. The
       refusal SAYS so: it used to return silently, and pressing Save did nothing at all. */
    guard: (f) => Boolean(f.arabicOrgName?.trim() && f.englishOrgName?.trim()),
    guardMessage: () => C.orgNamesRequired,
  },
  contact: {
    path: '/Settings/SaveContactSettings',
    body: (f) => ({
      ShowContactPage: Boolean(f.enableContactPage),
      ShowSendEmailForm: Boolean(f.showSendForm),
      ShowEmailInfo: Boolean(f.showEmailInfo),
      ShowPhoneInfo: Boolean(f.showPhone),
      ContactPhone: f.contactPhone ?? '',
      ContactEmail: f.contactEmail ?? '',
      ContactPageTitle: f.contactPageTitle ?? '',
      ContactPageTitleEn: f.contactPageTitleEn ?? '',
      ContactPageHeader: f.contactPageHeader ?? '',
      ContactPageHeaderEn: f.contactPageHeaderEn ?? '',
      ContactEmailLabelText: f.contactEmailLabel ?? '',
      ContactEmailEnLabelText: f.contactEmailEnLabel ?? '',
      ContactPhoneLabelText: f.contactPhoneLabel ?? '',
      ContactPhoneEnLabelText: f.contactPhoneEnLabel ?? '',
    }),
  },
  video: {
    path: '/Settings/SaveVideoSettings',
    body: (f) => ({
      EnableProgressBar: Boolean(f.progressBar),
      EnableForceSubtitle: Boolean(f.forceSubtitle),
      EnablePauseOnBlur: Boolean(f.pauseOnBlur),
      EnablePlaybackRates: Boolean(f.playbackRates),
      EnableUserSubtitleControl: Boolean(f.userSubtitleControl),
    }),
  },
  userManual: {
    path: '/Settings/SaveUserManualSettings',
    body: (f) => ({
      EnableUserManualPopup: Boolean(f.manualPopup),
      EnableIntroVideo: Boolean(f.introVideo),
      UserGuideLinkOnTheLoginPage: Boolean(f.guideLinkOnLogin),
      ShowUserGuideOnSeparatePage: Boolean(f.guideSeparatePage),
    }),
  },
  adminGuide: {
    path: '/Settings/SaveAdminUserGuide',
    body: (f) => ({ AdminPanelUserManualUrl: f.adminManualUrl ?? '' }),
  },
  reports: {
    path: '/Settings/SaveReportSettings',
    body: (f) => ({
      // The server stores these as one comma-joined string per report.
      ReportPolicies: (f.policiesColumns ?? []).join(','),
      ReportExams: (f.examsColumns ?? []).join(','),
      UserStatisticsPage: String(Boolean(f.userStatistics)),
    }),
  },

  servicesUrl: {
    path: '/Settings/SaveServicesUrl',
    body: (f) => ({ ServicesUrl: f.servicesUrl ?? '' }),
  },
  email: {
    path: '/Settings/SaveEmailSettings',
    body: (f) => emailBody(f),
  },
  whatsapp: {
    path: '/Settings/SaveWhatsappSettings',
    body: (f) => ({
      Enabled: Boolean(f.whatsappEnabled),
      ApiUrl: f.whatsappApiUrl ?? '',
      ApiKey: f.whatsappApiKey ?? '',
      SenderNumber: f.whatsappSender ?? '',
    }),
  },
  slack: {
    path: '/Settings/SaveSlackSettings',
    body: (f) => ({
      Enabled: Boolean(f.slackEnabled),
      WebhookUrl: f.slackWebhook ?? '',
      BotToken: f.slackBotToken ?? '',
      Channel: f.slackChannel ?? '',
    }),
  },
  sms: {
    path: '/Settings/SaveSmsSettings',
    body: (f) => ({
      Enabled: Boolean(f.smsEnabled),
      ApiUrl: f.smsApiUrl ?? '',
      ApiKey: f.smsApiKey ?? '',
      SenderId: f.smsSenderId ?? '',
    }),
  },

  /* ---- إعدادات متقدمة ----------------------------------------------------
     Everything here reads AND writes: the advanced view model returns each of these,
     unlike the organisation and registration cards on the system-settings screen. */
  general: {
    path: '/AdvancedSettings/SaveGeneralSettings',
    body: (f) => ({
      CompanyId: Number(f.companyId ?? 1),
      AuthenticationId: Number(f.authenticationId ?? AUTHENTICATION_METHOD.LOCAL),
      AuthenticationMode: f.authenticationMode ?? 'Forms',
      UsernameFormatOptions: String(f.usernameFormat ?? USERNAME_FORMAT_CODE.UNCHANGED),
    }),
  },
  otp: {
    path: '/AdvancedSettings/SaveOtpSettings',
    body: (f) => ({
      Enabled: Boolean(f.otpEnabled),
      Target: f.otpTarget ?? 'Admins',
      Length: String(f.otpLength ?? '4'),
      ValidPeriod: String(f.otpValidPeriod ?? '120'),
    }),
  },
  lockout: {
    path: '/AdvancedSettings/SaveAccountLockoutSettings',
    body: (f) => ({
      EnableAccountLockout: Boolean(f.lockoutEnabled),
      AttemptsBeforeLockout: Number(f.attempts ?? 5),
      AccountLockoutWindow: Number(f.lockoutWindow ?? 5),
      SessionTimeoutInMinutes: Number(f.sessionTimeout ?? 600),
    }),
  },
  terms: {
    path: '/AdvancedSettings/UpdateTerms',
    body: (f) => ({
      Arabic: f.termsAr ?? '',
      English: f.termsEn ?? '',
      IsEnabled: Boolean(f.termsEnabled),
      /* Marks the document as CHANGED, which makes everyone who already accepted it
         accept again — see the terms gate. Never inferred; the admin ticks it. */
      IsUpdated: Boolean(f.termsUpdated),
    }),
    /* Never ENABLE an empty document. This is the text every user in the installation has
       to accept before using it — the administrator included — and enabled empty it sent
       everyone to a blank page nobody could meaningfully agree to. Both languages, because
       each reader sees their own. The refusal says so: it used to return silently. */
    guard: (f) => !f.termsEnabled || Boolean(f.termsAr?.trim() && f.termsEn?.trim()),
    guardMessage: () => C.termsRequired,
  },
  /* THE SECOND CARD THAT HAD NO ROW HERE. `SaveRegistrationSettings` has existed in the
     Settings slice all along and binds exactly the seven fields this card collects; without
     the row, saving it reported "this setting is not available yet" — the same symptom the
     side-image card had, from the same cause.

     `activationBy` is the select's own `'1' | '2'`, seeded from `company.activateByAdmin`
     (AdvancedSettingsPage:1045). The command splits that one choice back into the two
     booleans the `Companies` row stores. */
  registration: {
    path: '/Settings/SaveRegistrationSettings',
    body: (f) => ({
      ActivateInvitations: Boolean(f.activateInvitations),
      ActivateResetPasswordPage: Boolean(f.activateResetPassword),
      ShowRegistrationLinkOnLoginPage: Boolean(f.showRegistrationLink),
      ActivateUserByAdmin: String(f.activationBy) === String(USER_ACTIVATION_METHOD.ADMIN),
      ActivateUserByEmail: String(f.activationBy) !== String(USER_ACTIVATION_METHOD.ADMIN),
      UserEmailOnDomain: Boolean(f.emailOnDomain),
      CompanyDomains: f.companyDomains ?? '',
    }),
  },
  otherProperties: {
    path: '/AdvancedSettings/UpdateProfileOtherProperties',
    body: (f) => ({
      /* The card's first toggle. It was rendered, flipped and never sent — the body carried
         the three below and nothing else, so `IsAllowToOpenProfilePageStatus` kept whatever
         it already had and the switch sprang back on the next load. */
      AllowProfileEditPage: Boolean(f.allowProfileEditPage),
      ShowRegistrationLinkOnLoginPage: Boolean(f.showRegistrationLink),
      ActivateResetPasswordPage: Boolean(f.activateResetPassword),
      ActivateInvitations: Boolean(f.activateInvitations),
      ForceGroupChange: Boolean(f.forceGroupChange),
      ForceChangeGroupId: String(f.forceGroupId ?? ''),
    }),
  },
  integration: {
    path: '/AdvancedSettings/SaveADIntegrationSettings',
    body: (f) => ({
      DepsDataSource: f.deptsSource ?? 'None',
      UsersDataSource: f.usersSource ?? 'None',
      UsersFetchDataSource: f.userFetchSource ?? 'None',
      UsersIntegrationType: f.usersType ?? 'Sync',
      WhenFetchedUsersCountGreaterThanLicense: f.whenFetchedUsersCountGreaterThanLicense ?? 'Reject',
      DeleteAdGroupsWithNoUsers: Boolean(f.deleteAdGroupsWithNoUsers),
    }),
  },
  autoIntegration: {
    path: '/AdvancedSettings/SaveAutoIntegrationSettings',
    body: (f) => ({
      AutoDeptsEnabled: String(f.deptsAutoFetch) === 'true',
      AutoDeptsSchedule: String(f.deptsAutoFetchInterval ?? ''),
      AutoUsersEnabled: String(f.usersAutoFetch) === 'true',
      AutoUsersSchedule: String(f.usersAutoFetchInterval ?? ''),
    }),
  },
  services: {
    path: '/AdvancedSettings/SetServicesSettings',
    body: (f) => ({
      ServiceEnabledNotifications: String(f.ServiceEnabledNotifications) === 'true',
      MaxTaskExecutionMinutes: String(f.MaxTaskExecutionMinutes ?? '10'),
      MaxRetries: String(f.MaxRetries ?? '3'),
    }),
  },

  /**
   * The profile fields, as a whole list.
   *
   * The card edits two sets of names — visible and editable — but the action replaces
   * every field's three flags at once, so the full list has to be rebuilt from what was
   * loaded. That is why this one takes the page's data as context: a body built from the
   * form alone would drop `IsRequired` and every field the card does not show.
   */
  profileFields: {
    path: '/AdvancedSettings/Update',
    body: (f, context) => ({
      CompanyId: context?.company?.id,
      Fields: (context?.profileFields ?? []).map((field) => ({
        ID: field.id,
        FieldName: field.name,
        IsVisible: (f.visibleFields ?? []).includes(field.name),
        IsEditable: (f.editableFields ?? []).includes(field.name),
        // Not on this card; carried through so saving does not clear it.
        IsRequired: Boolean(field.isRequired),
      })),
    }),
    guard: (f, context) => (context?.profileFields ?? []).length > 0,
  },

  /* Exactly five, ids 1-5 — the handler rejects any other count and switches on the id
     to pick the settings key pair. */
  customAttributes: {
    path: '/AdvancedSettings/SetCustomAttributes',
    body: (f) => ({
      Attributes: [1, 2, 3, 4, 5].map((index) => ({
        ID: index,
        AR_Name: f[`attribute${index}Ar`] ?? '',
        EN_Name: f[`attribute${index}En`] ?? '',
      })),
    }),
  },

  /* ---- the image cards ---------------------------------------------------
     `SaveLogo` takes the file inline — base64 content plus its name, size and
     type — rather than a multipart upload, so these post JSON like everything
     else and never touch `upload.js`. */
  logo: {
    path: '/Settings/SaveLogo',
    body: async (f) => ({
      ...(await fileFields(f.logo)),
      LogoBackground: String(f.logoBackground ?? 'true'),
      IsReporting: false,
    }),
  },
  reportLogo: {
    path: '/Settings/SaveLogo',
    file: 'reportLogo',
    body: async (f) => ({
      ...(await fileFields(f.reportLogo)),
      LogoBackground: String(f.logoBackground ?? 'true'),
      IsReporting: true,
    }),
  },
  favicon: {
    path: '/Settings/SaveFavIcon',
    file: 'favicon',
    body: async (f) => ({ FavIcon: await asBase64(f.favicon) }),
  },
  backgroundLogin: {
    path: '/Settings/SaveLoginBackground',
    body: async (f) => ({
      BackgroundAr: await asBase64(f.backgroundAr),
      BackgroundEn: await asBase64(f.backgroundEn),
    }),
    guard: (f) => Boolean(f.backgroundAr || f.backgroundEn),
  },
  /* The side images beside the sign-in form. The port carried `SaveLogo`, `SaveFavIcon` and
     `SaveLoginBackground` over from the legacy settings controller and left `UpdateSideLogin`
     behind, so this card had no row here and reported itself unavailable. The endpoint exists
     now — `SaveLoginSideImage`, the same two fixed filenames the legacy writes. */
  sideLogin: {
    path: '/Settings/SaveLoginSideImage',
    body: async (f) => ({
      Arabic: await asBase64(f.sideLoginAr),
      English: await asBase64(f.sideLoginEn),
    }),
    guard: (f) => Boolean(f.sideLoginAr || f.sideLoginEn),
  },
  userGuide: {
    path: '/Settings/SaveUserGuide',
    body: async (f) => ({
      Arabic: await asBase64(f.guideAr),
      English: await asBase64(f.guideEn),
    }),
    guard: (f) => Boolean(f.guideAr || f.guideEn),
  },
}

/**
 * @returns {Promise<{ ok: boolean, message: string }>} `ok:false` with the server's own
 *          words when it refused, so the card can say why rather than just stopping.
 */
export async function saveSection(section, values, context) {
  const spec = SECTIONS[section]

  // A card with no endpoint behind it — see COSMETIC_UI.md.
  if (!spec) return { ok: false, message: '', unsupported: true }

  if (spec.file && !values[spec.file]) return { ok: false, message: '', nothingToSave: true }
  if (spec.guard && !spec.guard(values, context)) {
    return spec.guardMessage
      ? { ok: false, message: spec.guardMessage() }
      : { ok: false, message: '', nothingToSave: true }
  }

  try {
    const data = await api.post(spec.path, await spec.body(values, context))
    /* THE ANSWER IS IN THE BODY, NOT THE STATUS. These handlers report a refusal — a
       missing file, an unwritable path, a caught exception — as HTTP 200 carrying
       `{ Result: false, Message }`, so a save that only checked for a thrown error
       toasted "saved" over the server's own error sentence and cleared the picked file
       with it. `saveNotificationTemplate` next door has always read `Result`. */
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** The eight fields `SaveEmailSettings` binds. `TestEmailSettings` takes the same, plus one. */
function emailBody(f) {
  return {
    Host: f.emailHost ?? '',
    Port: String(f.emailPort ?? ''),
    From: f.emailFrom ?? '',
    SenderName: f.emailSenderName ?? '',
    Username: f.emailUsername ?? '',
    Password: f.emailPassword ?? '',
    SslOptions: f.emailSslOptions ?? '',
    ServiceState: String(f.emailServiceState ?? ''),
  }
}

/**
 * Sends a probe message with the settings CURRENTLY IN THE FORM, not the stored ones —
 * which is the point: it answers "will this work" before anyone commits it.
 *
 * `address` is where the probe lands. Left empty the server sends to the sender address,
 * which is what this action did before it took one.
 */
export async function testEmailSettings(values, address = '') {
  try {
    const data = await api.post('/Settings/TestEmailSettings', {
      ...emailBody(values),
      DisableServerCertificateValidation: String(values.disableCertValidation) === 'true',
      Address: address ?? '',
    })
    return { ok: true, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export async function saveNotificationTemplate(template) {
  try {
    const data = await api.post('/Settings/SaveEmailTemplate', {
      Id: Number(template.id),
      Subject: template.subject ?? '',
      Content: template.content ?? '',
    })
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** One channel's stored JSON blob, as plain fields. */
export function parseChannel(raw) {
  try {
    const parsed = JSON.parse(raw || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    // An administrator can put anything in a settings row; a broken blob is an empty card.
    return {}
  }
}

/** `SaveLogoCommand` wants the content, the name, the size and the MIME type. */
async function fileFields(file) {
  return {
    File: await asBase64(file),
    Name: file?.name ?? '',
    Size: file?.size ?? 0,
    Type: file?.type ?? '',
  }
}

/** Base64 WITHOUT the `data:` prefix, which is what the handlers decode. */
function asBase64(file) {
  if (!file) return Promise.resolve('')

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read the selected file'))
    reader.onload = () => resolve(String(reader.result).split(',').pop() ?? '')
    reader.readAsDataURL(file)
  })
}

/* ---- still mocked: their own slices ------------------------------------- */

/**
 * `إعدادات متقدمة` — `Features/Admin/SettingsManagement/AdvancedSettings/Endpoint.cs`.
 *
 *   getAdvanced -> GET /api/advanced-settings
 *
 * The data sources and file-storage servers still come from the mock: they have their own
 * GET actions (`/AdvancedSettings/GetDataSources`, `/GetFileStorageServers`) and their own
 * create / delete / test flows, which are wired with the collection cards in part 2.
 */
export async function getAdvanced(signal) {
  const dto = (await api.get('/api/advanced-settings', { signal })) ?? {}
  const bool = (value) => (value ? 'true' : 'false')
  const authMethods = (dto.AuthenticationMethods ?? []).map((method) => ({
    id: method.AuthenticationId,
    name: method.AuthenticationId === AUTHENTICATION_METHOD.ENTRA_ID
      ? 'Microsoft Entra ID'
      : method.AuthenticationId === AUTHENTICATION_METHOD.ENTRA_ID_AND_LOCAL
        ? 'Microsoft Entra ID + Local'
        : method.AuthenticationName ?? '',
  }))
  const authenticationId = authMethods.some((method) =>
    String(method.id) === String(dto.AuthenticationId))
    ? dto.AuthenticationId
    : AUTHENTICATION_METHOD.LOCAL
  const authenticationMode = String(dto.AuthenticationMode ?? '').toLowerCase() === 'windows'
    ? 'Windows'
    : 'Forms'

  /* Two collections have their own GET actions rather than riding on the page payload,
     so they are fetched alongside it. Both are small and neither blocks the other. */
  const [dataSources, servers] = await Promise.all([
    api.get('/AdvancedSettings/GetDataSources', { signal }).catch(() => []),
    api.get('/AdvancedSettings/GetFileStorageServers', { signal }).catch(() => []),
  ])

  return {
    settings: {
      LicenseType: dto.LicenseType ?? '',
      AuthenticationMode: authenticationMode,
      UsernameFormatOptions: dto.UsernameFormatOptions ?? USERNAME_FORMAT_CODE.UNCHANGED,

      OTPEnabled: bool(dto.OtpEnabled),
      OTPTarget: dto.OtpTarget ?? 'Admins',
      OTPLength: dto.OtpLength ?? '4',
      OTPValidPeriod: dto.OtpValidPeriod ?? '120',

      EnableAccountLockout: bool(dto.EnableAccountLockout),
      AttemptsBeforeLockout: String(dto.AttemptsBeforeLockout ?? 5),
      AccountLockoutWindow: String(dto.AccountLockoutWindow ?? 5),
      SessionTimeoutInMinutes: String(dto.SessionTimeoutInMinutes ?? 600),

      DepartmentsIntegrationDataSource: dto.DepartmentsIntegrationDataSource ?? 'None',
      DepartmentsIntegrationType: dto.DepartmentsIntegrationType ?? 'Import',
      UsersIntegrationDataSource: dto.UsersIntegrationDataSource ?? 'None',
      UsersIntegrationType: dto.UsersIntegrationType ?? 'Sync',
      UserFetchInfoDataSource: dto.UserFetchInfoDataSource ?? 'None',
      UserFetchInfoType: dto.UserFetchInfoType ?? 'Sync',

      DeptsAutoFetch: dto.DeptsAutoFetch ?? 'false',
      DeptsAutoFetchInterval: dto.DeptsAutoFetchInterval ?? '180',
      UsersAutoFetch: dto.UsersAutoFetch ?? 'false',
      UsersAutoFetchInterval: dto.UsersAutoFetchInterval ?? '1200',
      WhenFetchedUsersCountGreaterThanLicense: dto.WhenFetchedUsersCountGreaterThanLicense ?? 'Reject',
      DeleteADGroupsWithNoUsers: bool(dto.DeleteAdGroupsWithNoUsers),
      SyncProfilesCommandMode: dto.SyncProfilesCommandMode ?? 'bulkSql',

      /* The card reads these five pairs by settings-key name. The server returns them
         as a list of `{ ArNameKey, EnNameKey, ArValue, EnValue }` — the keys ARE the settings
         names — so they are spread back out here. This read `ID`/`AR_Name`/`EN_Name`, which
         the payload does not carry: every name fell back to "AttributeN", and pressing Update
         wrote those defaults over the names an administrator had set. */
      ...Object.fromEntries(
        (dto.CustomAttributes ?? []).flatMap((attribute) => [
          [attribute.ArNameKey, attribute.ArValue ?? ''],
          [attribute.EnNameKey, attribute.EnValue ?? ''],
        ]).filter(([key]) => Boolean(key)),
      ),

      ActivateInvitations: bool(dto.ActivateInvitations),
      ShowRegistrationLinkOnLoginPage: bool(dto.ShowRegistrationLinkOnLoginPage),
      ActivateResetPasswordPage: bool(dto.ActivateResetPasswordPage),

      /* THE SERVICES, AS STORED. The view model has always carried every one of them in
         `ServicesSettings` and nothing read it, so the services section opened on hard-coded
         defaults — and the notification service read "on" whatever the database held, so
         saving that section could switch a stopped service back on. The DTO's names are
         already the keys the section reads (`ServiceEnabledNotificationsCreation`, …),
         except the service switch itself, which is the `Email_ServiceState` key. */
      ...Object.fromEntries(
        Object.entries(dto.ServicesSettings ?? {}).filter(([key]) => key !== 'ServiceEnabledNotifications'),
      ),
      Email_ServiceState: String(dto.ServicesSettings?.ServiceEnabledNotifications ?? 'false').toLowerCase(),
    },
    company: {
      id: dto.CompanyId ?? 1,
      authenticationId,
      /* The organisation picker on the profile card named its one option with a field the
         company object did not have, and showed a blank choice. */
      name: (dto.Companies ?? []).find((company) => company.CompanyId === (dto.CompanyId ?? 1))?.CompanyName ?? '',
    },
    /* The three flags on the profile card ARE readable here — unlike the same settings on
       the system-settings screen, which is why the registration card lives on this page. */
    profileOther: {
      allowProfileEditPage: Boolean(dto.AllowProfileEditPage),
      enableForceGroupChange: Boolean(dto.EnableForceGroupChange),
      forceChangeGroupId: dto.ForceChangeGroupId ?? '',
    },
    /* `TermsOfUse`, not `Terms` — reading the wrong property here left the card empty,
       and its save would then have blanked the document every user has to accept. */
    terms: {
      arabic: dto.TermsOfUse?.Arabic ?? '',
      english: dto.TermsOfUse?.English ?? '',
      isEnabled: Boolean(dto.TermsOfUse?.IsEnabled),
      isUpdated: Boolean(dto.TermsOfUse?.IsUpdated),
    },
    profileFields: (dto.UserProfileFields ?? []).map((field) => ({
      id: field.ID ?? field.Id,
      name: field.BuiltInAttributeName ?? '',
      isVisible: Boolean(field.IsVisible),
      isRequired: Boolean(field.IsRequired),
      isEditable: Boolean(field.IsEditable),
    })),
    customAttributes: (dto.CustomAttributes ?? []).map((attribute, index) => ({
      id: index + 1,
      arabicName: attribute.ArValue ?? '',
      englishName: attribute.EnValue ?? '',
    })),
    authMethods,
    groups: (dto.Departments ?? []).map((group) => ({
      id: group.GroupId,
      name: (isEnglish ? group.EnglishName : group.ArabicName) || group.ArabicName || '',
    })),
    dataSources: (dataSources ?? []).map((source) => ({
      id: source.Id,
      type: source.Type ?? '',
      arabicName: source.ArabicName ?? '',
      englishName: source.EnglishName ?? '',
      username: source.Username ?? '',
      connectionString: source.ConnectionString ?? '',
      domain: source.Domain ?? '',
      filters: source.Filters ?? '',
      ssl: Boolean(source.SSL),
      isActive: Boolean(source.IsActive),
      hasCredential: Boolean(source.HasCredential),
      limitAccessToGroup: source.LimitAccessToGroup ?? '',
      adminGroups: source.AdminGroups ?? '',
      userResignedIfMissing: Boolean(source.UserResignedIfMissing),
      updateUserIdIfChangeDetected: Boolean(source.UpdateUserIdIfChangeDetected),
      resignedUsersAction: resignedAction(source),
      detectResignedInData: Boolean(source.DetectResignedUsersInReturnADData),
      resignedIdentification: source.ResignedUsersIdentification ?? '',
      profileAttributesMapping: source.ProfileAttributesMapping ?? [],
    })),
    servers: (servers ?? []).map((server) => ({
      id: server.Id,
      serverId: server.ServerId,
      serverAddress: server.ServerAddress ?? '',
    })),
  }
}

/**
 * A file-storage server: its number and address. Both actions existed with nothing on the
 * screen to call them — the servers section was a read-only list.
 */
export async function saveFileServer(values) {
  try {
    const data = await api.post('/AdvancedSettings/SaveFileStorageServer', {
      Id: Number(values.id || 0),
      ServerId: Number(values.serverId || 0),
      ServerUrl: values.serverAddress?.trim() ?? '',
    })
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    return { ok: false, message: error instanceof ApiError ? error.message : '' }
  }
}

export async function deleteFileServer(id) {
  try {
    const data = await api.post('/AdvancedSettings/DeleteFileStorageServer', { Id: Number(id) })
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    return { ok: false, message: error instanceof ApiError ? error.message : '' }
  }
}

/* A source saved before the form offered the action has none. The sync then flags
   resigned users for an administrator, so that is what the form shows — unless the
   source has no way to find one, which is no action at all. */
const RESIGNED_ACTIONS = ['NoAction', 'AutoHide', 'ByAdmin']
const resignedAction = (source) => {
  const saved = RESIGNED_ACTIONS.find((action) => action.toLowerCase() === (source.ResignedUsersAction ?? '').trim().toLowerCase())
  if (saved) return saved
  return source.UserResignedIfMissing || source.DetectResignedUsersInReturnADData ? 'ByAdmin' : 'NoAction'
}

export async function saveDataSource(values) {
  try {
    const data = await api.post('/AdvancedSettings/SaveDataSource', {
      Id: Number(values.id || 0),
      Name: values.name?.trim() ?? '',
      Type: values.type ?? 'LDAP',
      ConnectionString: values.connectionString?.trim() ?? '',
      Username: values.username?.trim() ?? '',
      Password: values.password ?? '',
      Domain: values.domain?.trim() ?? '',
      Filters: values.filters?.trim() ?? '',
      SSL: Boolean(values.ssl),
      IsActive: Boolean(values.isActive),
      LimitAccessToGroup: values.limitAccessToGroup?.trim() ?? '',
      AdminGroups: values.adminGroups?.trim() ?? '',
      UserResignedIfMissing: Boolean(values.userResignedIfMissing),
      UpdateUserIdIfChangeDetected: Boolean(values.updateUserIdIfChangeDetected),
      // Omitted, the server keeps the saved resignation settings.
      ResignedUsersAction: values.resignedUsersAction ?? null,
      DetectResignedUsersInReturnADData: Boolean(values.detectResignedInData),
      ResignedUsersIdentification: values.resignedIdentification?.trim() ?? '',
    })
    return { ok: data?.Result === true, message: data?.Message ?? '' }
  } catch (error) {
    return { ok: false, message: error instanceof ApiError ? error.message : '' }
  }
}

/**
 * Removes a data source. The action existed and the screen offered no way to reach it, so a
 * source — a legacy one the form cannot even save — could never be taken away.
 */
export async function deleteDataSource(id) {
  try {
    const data = await api.post('/AdvancedSettings/DeleteDataSource', { DataSourceId: Number(id) })
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    return { ok: false, message: error instanceof ApiError ? error.message : '' }
  }
}

export async function testDirectorySource(values) {
  try {
    const data = await api.post('/AdvancedSettings/TestActiveDirectory', {
      Id: Number(values.id || 0),
      Type: values.type ?? 'LDAP',
      ConnectionString: values.connectionString?.trim() ?? '',
      Username: values.username?.trim() ?? '',
      Password: values.password ?? '',
      Domain: values.domain?.trim() ?? '',
      Filters: values.filters?.trim() ?? '',
      SSL: Boolean(values.ssl),
    })
    return { ok: true, message: data?.Message ?? '', properties: data?.Properties ?? [] }
  } catch (error) {
    return { ok: false, message: error instanceof ApiError ? error.message : '' }
  }
}

/** A data source's name in the reader's language, falling back to the other one. */
export const sourceName = (source) =>
  (isEnglish ? source?.englishName || source?.arabicName : source?.arabicName || source?.englishName) || ''

/* One account as the directory holds it. The API answers in PascalCase (`DataSourceUserDto`);
   the table read camelCase straight off the payload, so every cell was undefined and the
   browse showed rows of nothing - and every account "active", resigned or not. */
const toDirectoryUser = (user) => ({
  username: user.Username ?? '',
  email: user.Email ?? '',
  arabicName: user.ArabicName ?? '',
  englishName: user.EnglishName ?? '',
  department: user.Department ?? '',
  phone: user.Phone ?? '',
  mobile: user.Mobile ?? '',
  employeeId: user.EmployeeId ?? '',
  isResigned: Boolean(user.IsResigned),
})

export async function getDataSourceUsers(id) {
  try {
    const data = await api.get(`/AdvancedSettings/GetDataSourceUsers?id=${id}`)
    return { ok: data?.Result !== false, data: (data?.Data ?? []).map(toDirectoryUser), message: data?.Message ?? '' }
  } catch (error) {
    return { ok: false, data: [], message: error instanceof ApiError ? error.message : '' }
  }
}

/* A department as the directory gives it (`DataSourceGroupDto`: Id, ArabicName, EnglishName,
   in PascalCase - read as camelCase it came out blank): the pair of names its users carry. */
const toDirectoryDepartment = (dept) => ({
  id: dept.Id ?? '',
  arabicName: dept.ArabicName ?? '',
  englishName: dept.EnglishName ?? '',
})

export async function getDataSourceGroups(id) {
  try {
    const data = await api.get(`/AdvancedSettings/GetDataSourceGroups?id=${id}`)
    return { ok: data?.Result !== false, data: (data?.Data ?? []).map(toDirectoryDepartment), message: data?.Message ?? '' }
  } catch (error) {
    return { ok: false, data: [], message: error instanceof ApiError ? error.message : '' }
  }
}

export async function queryDataSourceProperties(id, username) {
  try {
    const data = await api.post('/AdvancedSettings/QueryDataSourceProperties', {
      DataSourceId: Number(id),
      Username: username?.trim() || null,
    })
    return {
      ok: data?.Result !== false,
      message: data?.Message ?? '',
      properties: data?.Data?.properties ?? [],
      propertyPairs: data?.Data?.PropertyPairs ?? [],
      formattedHtml: data?.Data?.ADProperties ?? '',
    }
  } catch (error) {
    return { ok: false, message: error instanceof ApiError ? error.message : '', properties: [], propertyPairs: [], formattedHtml: '' }
  }
}

export async function saveProfileMapping(id, mappings) {
  try {
    const data = await api.post('/AdvancedSettings/SaveProfileMapping', {
      DataSourceId: Number(id),
      Mappings: mappings,
    })
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    return { ok: false, message: error instanceof ApiError ? error.message : '' }
  }
}

/**
 * `بيانات الإعدادات` — every settings key with its stored and default value, plus the
 * reference tables the platform's enums are supposed to mirror.
 *
 * The second part is a CONSISTENCY CHECK, not settings: each enum the code compiles
 * against is listed against the database rows that should back it, so a missing activity
 * type or authentication method is visible here rather than as a blank label somewhere
 * else. The Razor page showed it; the React screen did not, and now does.
 */
export async function listSettingsKeys(signal) {
  const dto = (await api.get('/api/settings-keys', { signal })) ?? {}

  /* `table` is the list's name in the API — the view model's property, which is also what
     `saveReferenceEntry` names it by. Notification templates have none: they are edited on
     their own screen and only checked here. */
  const reference = (rows, group, table) =>
    (rows ?? []).map((row) => ({
      group,
      table,
      id: row.Id,
      enumName: row.EnumName ?? '',
      arabicName: row.DbArabicName ?? '',
      englishName: row.DbEnglishName ?? '',
      // Authentication methods carry one name rather than two.
      name: row.DbName ?? '',
      existsInDb: Boolean(row.ExistsInDb),
    }))

  return {
    rows: (dto.Settings ?? []).map((row) => ({
      key: row.Key,
      // `null` and `""` mean different things here: never written vs written empty.
      dbValue: row.DbValue ?? null,
      defaultValue: row.DefaultValue ?? null,
      /* A credential arrives as a mask, so it cannot be edited here — saving would write
         the mask over the secret. The server refuses it too. */
      sensitive: Boolean(row.IsSensitive),
    })),
    reference: [
      ...reference(dto.ActivityEntries, 'activities', 'ActivityEntries'),
      ...reference(dto.GroupTypes, 'groupTypes', 'GroupTypes'),
      ...reference(dto.DifficultyLevels, 'difficultyLevels', 'DifficultyLevels'),
      ...reference(dto.CourseLevels, 'courseLevels', 'CourseLevels'),
      ...reference(dto.AuthenticationMethods, 'authMethods', 'AuthenticationMethods'),
      ...reference(dto.NotificationTemplates, 'templates', null),
    ],
    /* Whether the video toolchain is present. A false here explains a whole class of
       "the video did not process" reports, so it is worth a line on this page. */
    ffmpegExists: Boolean(dto.FfmpegExists),
  }
}

/**
 * Stores one key's value, as typed. Answers `{ Result, Message }`; a refusal — an unknown
 * key, a credential, an empty value — is `Result: false` with the reason.
 */
export async function saveSettingsKeyValue(key, value) {
  try {
    const data = await api.post('/api/settings-keys/value', { Key: key, Value: value })
    return { ok: data?.Result === true, message: data?.Message ?? '' }
  } catch (error) {
    return { ok: false, message: error instanceof ApiError ? error.message : '' }
  }
}

/**
 * Names one reference row, adding it under its enum id when the database lacks it.
 *
 * @param {{table: string, id: number}} row  a row from `listSettingsKeys().reference`
 * @param {{arabicName?: string, englishName?: string, name?: string}} names
 */
export async function saveReferenceEntry(row, names) {
  try {
    const data = await api.post('/api/settings-keys/reference', {
      Table: row.table,
      Id: row.id,
      ArabicName: names.arabicName ?? null,
      EnglishName: names.englishName ?? null,
      Name: names.name ?? null,
    })
    return { ok: data?.Result === true, message: data?.Message ?? '' }
  } catch (error) {
    return { ok: false, message: error instanceof ApiError ? error.message : '' }
  }
}

/* ---- Product documentation --------------------------------------------------------- */

/**
 * Which guides this installation has, in the panel's language.
 *
 * `GET /api/documentation/{audience}/{language}` answers `{ available, pdfUrl, htmlUrl }`
 * for an installed guide, 404 for a missing one and 403 for a guide this account may not
 * read — both of which mean "nothing to open" here.
 */
export async function getDocumentation() {
  const language = isEnglish ? 'en' : 'ar'
  const guides = {}
  await Promise.all(
    ['admin', 'user'].map(async (audience) => {
      try {
        const dto = await api.get(`/api/documentation/${audience}/${language}`)
        guides[audience] = dto?.available ? { htmlUrl: dto.htmlUrl, pdfUrl: dto.pdfUrl } : null
      } catch (error) {
        if (error instanceof ApiError) guides[audience] = null
        else throw error
      }
    }),
  )
  return guides
}

/**
 * Import a signed documentation bundle — the zip itself as the body. The header is the
 * server's CSRF check for this endpoint; it refuses without it.
 */
export async function importDocumentation(file) {
  try {
    await api.post('/api/documentation/import', file, {
      headers: { 'Content-Type': 'application/zip', 'X-Rawam-Documentation-Import': '1' },
    })
    return { ok: true }
  } catch (error) {
    if (!(error instanceof ApiError)) throw error
    /* THE STATUS IS THE WHOLE ANSWER. `DocumentationEndpoints.Import` refuses with a bare
       code and no body — 413 for a bundle over 150 MB, 503 when the installation has no
       trusted signing key or release configured, 400 for everything about the bundle
       itself. Reported as one sentence about the signature, two of the three sent the
       administrator looking in the wrong place. */
    if (error.status === HTTP_STATUS.PAYLOAD_TOO_LARGE) return { ok: false, message: S.tooLarge }
    if (error.status === HTTP_STATUS.SERVICE_UNAVAILABLE) return { ok: false, message: S.notConfigured }
    return { ok: false, message: error.message }
  }
}
